import { useCallback, useEffect, useId, useState } from 'react';
import type { UnmatchedFilters, UnmatchedList } from '../../../services/src/shared/api-types.ts';
import type { Api } from '../api.ts';
import type { ScreenProps } from '../App.tsx';
import { day, daysAgo, money, num, when } from '../format.ts';
import { Empty, ErrorBanner, Loading, useDebounced } from '../ui.tsx';

export function Unmatched({ api }: ScreenProps) {
  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Unmatched</h1>
          <p className="lede">Bank and card lines the rules couldn’t pair with a Salesforce payment. Most need a payment logged in Salesforce.</p>
        </div>
      </header>
      <div className="panel-stack">
        <BankLines api={api} />
      </div>
    </div>
  );
}

function BankLines({ api }: { api: Api }) {
  const [search, setSearch] = useState('');
  const query = useDebounced(search);
  const [account, setAccount] = useState('');
  const [olderThan, setOlderThan] = useState('');
  const [list, setList] = useState<UnmatchedList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const ids = { search: useId(), account: useId(), age: useId() };

  const load = useCallback(() => {
    const filters: UnmatchedFilters = {
      kind: 'line',
      account: account || undefined,
      q: query || undefined,
      to: olderThan ? daysAgo(Number(olderThan)) : undefined,
    };
    let live = true;
    setLoading(true);
    setError(null);
    api.unmatched(filters).then(
      (r) => live && (setList(r), setLoading(false)),
      (e: Error) => live && (setError(e.message), setLoading(false)),
    );
    return () => {
      live = false;
    };
  }, [api, account, query, olderThan]);
  useEffect(load, [load]);

  return (
    <>
      <div className="filters">
        <label className="field grow" htmlFor={ids.search}>
          Search
          <input id={ids.search} type="search" placeholder="Description or amount" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <label className="field account" htmlFor={ids.account}>
          Account
          <select className="select" id={ids.account} value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">All accounts</option>
            {(list?.accounts ?? []).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label className="field age" htmlFor={ids.age}>
          Older than
          <select className="select" id={ids.age} value={olderThan} onChange={(e) => setOlderThan(e.target.value)}>
            <option value="">Any age</option>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
          </select>
        </label>
      </div>

      <div aria-busy={loading} className="panel-stack">
        {list?.asOf && <p className="small muted">As of {when(list.asOf)}.</p>}
        {error && <ErrorBanner message={error} onRetry={load} />}
        {!list && !error && <Loading kind="table" />}
        {list && list.items.length === 0 && !error && (
          <Empty>{list.asOf ? 'Nothing unmatched for these filters.' : 'No matching run yet. The list fills in after the first sync with Salesforce.'}</Empty>
        )}
        {list && list.items.length > 0 && (
          <div className="card table-card">
            <table className="table min-640">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Description</th>
                  <th scope="col">Account</th>
                  <th scope="col">Why</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((u) => (
                  <tr key={u.id}>
                    <td className="date">{day(u.date)}</td>
                    <td>{u.description}</td>
                    <td className="acct">{u.account || <span className="muted">No feed</span>}</td>
                    <td>
                      <span className="chip">{u.amount < 0 ? 'Money in, nothing logged' : 'No payment logged'}</span>
                    </td>
                    <td className="num">{money(u.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list && list.items.length < list.total && (
          <p className="small muted table-foot">
            Showing {num(list.items.length)} of {num(list.total)}. Search or pick an account to narrow it down.
          </p>
        )}
      </div>
    </>
  );
}
